'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowUpDownIcon,
  Cancel01Icon,
  RefreshIcon,
  Tick01Icon,
} from '@terra/ui-icons';
import type { PreventionWafTarget } from '@terra/shared-contracts-integrations';
import { Badge } from '../../../components/badge';
import { Combobox } from '../../../components/combobox';
import { Field } from '../../../components/field';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Spinner } from '../../../components/spinner';
import { createSelectListCollection } from '../../../components/select';
import {
  BodyDefault,
  BodyStrong,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';

export type PreventionTargetDiscovery = {
  targets: readonly PreventionWafTarget[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRefresh: () => void;
};

export type PreventionTargetFieldLabels = {
  select: string;
  placeholder: string;
  discovering: string;
  noneFound: string;
  discoverFailed: string;
  refresh: string;
  confirm: string;
  clear: string;
  recommended?: string;
};

type PreventionTargetOption = {
  value: string;
  label: string;
};

export type PreventionTargetFieldProps = {
  discovery: PreventionTargetDiscovery;
  discoveryKey?: string;
  value: string;
  onChange: (ref: string) => void;
  isReadOnly?: boolean;
  recommendedRef?: string | null;
  labels: PreventionTargetFieldLabels;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  onClear?: () => void;
  clearLoading?: boolean;
};

const toOptionLabel = (target: PreventionWafTarget): string =>
  target.description ? `${target.label} · ${target.description}` : target.label;

export function PreventionTargetField({
  discovery,
  discoveryKey,
  value,
  onChange,
  isReadOnly,
  recommendedRef,
  labels,
  onConfirm,
  confirmDisabled,
  confirmLoading,
  onClear,
  clearLoading,
}: PreventionTargetFieldProps) {
  const { targets, isLoading, isFetching, isError, onRefresh } = discovery;
  const hasAutoSelectedRef = useRef(false);
  const hasResults = targets.length > 0;
  const options = useMemo<PreventionTargetOption[]>(
    () =>
      targets.map((target) => ({
        value: target.ref,
        label: toOptionLabel(target),
      })),
    [targets],
  );
  const labelForValue =
    options.find((option) => option.value === value)?.label ?? value;
  const [inputValue, setInputValue] = useState(labelForValue);
  const selectedFromList = options.some((option) => option.value === value);
  const comboboxValue = useMemo(
    () => (selectedFromList && value ? [value] : []),
    [selectedFromList, value],
  );

  useEffect(() => {
    setInputValue(labelForValue);
  }, [labelForValue]);
  useEffect(() => {
    hasAutoSelectedRef.current = false;
  }, [discoveryKey]);
  useEffect(() => {
    if (hasAutoSelectedRef.current || value || !hasResults) {
      return;
    }

    const recommended = recommendedRef
      ? targets.find((target) => target.ref === recommendedRef)
      : undefined;
    const candidate = recommended ?? (targets.length === 1 ? targets[0] : null);

    if (candidate) {
      hasAutoSelectedRef.current = true;
      onChange(candidate.ref);
    }
  }, [hasResults, onChange, recommendedRef, targets, value]);

  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: options,
        itemToString: (option: PreventionTargetOption) => option.label,
        itemToValue: (option: PreventionTargetOption) => option.value,
      }),
    [options],
  );
  const discoveryHint = isError
    ? labels.discoverFailed
    : !hasResults
      ? labels.noneFound
      : null;
  const canClear = Boolean(value.trim()) && !isReadOnly;
  const handleClear = () => {
    if (onClear) {
      onClear();

      return;
    }

    setInputValue('');
    onChange('');
  };

  return (
    <Field.Root>
      <Field.Label>
        <HStack gap={2} alignItems="center">
          <LabelDefault as="span">{labels.select}</LabelDefault>
          {isFetching && !isLoading ? <Spinner size="sm" /> : null}
        </HStack>
      </Field.Label>

      {isLoading ? (
        <HStack gap={2} alignItems="center">
          <Spinner size="sm" />
          <BodyDefault color="fg.muted">{labels.discovering}</BodyDefault>
        </HStack>
      ) : (
        <VStack gap={2} alignItems="stretch">
          <HStack gap={1} alignItems="center">
            <Combobox.Root
              width="full"
              flex={1}
              collection={collection}
              value={comboboxValue}
              inputValue={inputValue}
              allowCustomValue
              openOnClick
              disabled={isReadOnly}
              positioning={{ sameWidth: true, strategy: 'fixed' }}
              onInputValueChange={({ inputValue: nextInputValue }) => {
                setInputValue(nextInputValue);
                const isKnownLabel = options.some(
                  (option) => option.label === nextInputValue,
                );

                if (!isKnownLabel) {
                  onChange(nextInputValue);
                }
              }}
              onValueChange={({ value: selected }) => {
                const nextRef = selected[0] ?? '';
                const nextLabel =
                  options.find((option) => option.value === nextRef)?.label ??
                  nextRef;

                setInputValue(nextLabel);
                onChange(nextRef);
              }}
            >
              <Combobox.Control position="relative" width="full">
                <Combobox.Input placeholder={labels.placeholder} asChild>
                  <Input size="sm" pe="10" width="full" />
                </Combobox.Input>
                <Combobox.Trigger asChild>
                  <IconButton
                    variant="link"
                    aria-label={labels.select}
                    size="xs"
                    position="absolute"
                    right="2"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={1}
                    pointerEvents="auto"
                  >
                    <ArrowUpDownIcon size={16} />
                  </IconButton>
                </Combobox.Trigger>
              </Combobox.Control>
              <Combobox.Positioner>
                <Combobox.Content maxHeight="300px" overflowY="auto">
                  <Combobox.ItemGroup>
                    {collection.items.map((option) => (
                      <Combobox.Item
                        key={option.value}
                        item={option}
                        height="auto"
                        py={1}
                      >
                        <VStack
                          flex="1"
                          gap={0}
                          alignItems="flex-start"
                          overflow="hidden"
                          minW={0}
                        >
                          <HStack
                            gap={2}
                            alignItems="center"
                            justifyContent="space-between"
                            w="full"
                          >
                            <Combobox.ItemText asChild>
                              <BodyStrong w="full" truncate>
                                {option.label}
                              </BodyStrong>
                            </Combobox.ItemText>
                            {labels.recommended &&
                            recommendedRef &&
                            option.value === recommendedRef ? (
                              <Badge size="sm" variant="outline">
                                {labels.recommended}
                              </Badge>
                            ) : null}
                          </HStack>
                          <MetaDefault color="fg.muted" w="full" truncate>
                            {option.value}
                          </MetaDefault>
                        </VStack>
                        <Combobox.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Combobox.ItemIndicator>
                      </Combobox.Item>
                    ))}
                  </Combobox.ItemGroup>
                </Combobox.Content>
              </Combobox.Positioner>
            </Combobox.Root>
            {onConfirm ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={labels.confirm}
                onClick={onConfirm}
                loading={confirmLoading}
                disabled={confirmDisabled || isReadOnly}
              >
                <Tick01Icon size={16} />
              </IconButton>
            ) : null}
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={labels.clear}
              onClick={handleClear}
              loading={clearLoading}
              disabled={!canClear && !clearLoading}
            >
              <Cancel01Icon size={16} />
            </IconButton>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={labels.refresh}
              onClick={onRefresh}
              loading={isFetching}
              disabled={isReadOnly}
            >
              <RefreshIcon size={16} />
            </IconButton>
          </HStack>
          {discoveryHint ? (
            <MetaDefault color="fg.muted">{discoveryHint}</MetaDefault>
          ) : null}
        </VStack>
      )}
    </Field.Root>
  );
}
